import React, { useState, useEffect } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, LightBulbIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const Flashcard = ({ question, onAnswer }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  const [imageError, setImageError] = useState({
    question: false,
    answer: false,
    explanation: false
  });

  // Funktion zum Formatieren der Tiefenangaben
  const formatDepthNumbers = (text) => {
    // Sucht nach Zahlen wie "23" und stellt die zweite Ziffer tief
    return text.replace(/\b(\d)(\d)\b(?=\.?\s+Was bedeutet das\?)/g, '$1<sub>$2</sub>');
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        setShowAnswer(prev => !prev);
      }
      
      if (showAnswer) {
        if (event.key === 'r') {
          handleAnswer(true);
        } else if (event.key === 'f') {
          handleAnswer(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  });

  const handleAnswer = (isCorrect) => {
    onAnswer(isCorrect);
    setShowAnswer(false);
  };

  const handleImageError = (type) => {
    setImageError(prev => ({
      ...prev,
      [type]: true
    }));
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Main Content Area */}
      <div className="p-4 space-y-4">
        {/* Question Section */}
        <div className="card bg-base-100 shadow-sm w-full">
          <div className="card-body p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-primary">Frage</h3>
                <span className="badge badge-primary badge-outline text-xs">
                  {question.is_exam_relevant ? 'Prüfungsrelevant' : 'Nicht Prüfungsrelevant'}
                </span>
              </div>
            </div>

            <div className="prose max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {formatDepthNumbers(question.question_text)}
              </ReactMarkdown>
            </div>

            {question.question_image && !imageError.question && (
              <div className="relative group">
                <img
                  src={question.question_image}
                  alt="Zur Frage"
                  className="rounded-lg shadow-sm max-h-[30vh] w-full object-contain bg-base-200"
                  onError={() => handleImageError('question')}
                />
                <div className="absolute inset-0 bg-base-300/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button className="btn btn-primary btn-sm" onClick={() => window.open(question.question_image, '_blank')}>
                    Vergrößern
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Answer Section */}
        {showAnswer ? (
          <div className="card bg-base-100 shadow-sm w-full">
            <div className="card-body p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-primary">Antwort</h3>
                {showAnswer && (
                  <button
                    onClick={() => setShowAnswer(false)}
                    className="btn btn-ghost btn-sm gap-2"
                    title="Antwort ausblenden"
                  >
                    <EyeSlashIcon className="h-5 w-5" />
                    <span className="sm:inline">Ausblenden</span>
                    <kbd className="kbd kbd-xs hidden lg:inline-flex">Leerzeichen</kbd>
                  </button>
                )}
              </div>
              
              <div className="prose max-w-none">
                <ReactMarkdown>
                  {question.answer_text}
                </ReactMarkdown>
              </div>

              {question.answer_image && !imageError.answer && (
                <div className="relative group">
                  <img
                    src={question.answer_image}
                    alt="Zur Antwort"
                    className="rounded-lg shadow-sm max-h-[30vh] w-full object-contain bg-base-200"
                    onError={() => handleImageError('answer')}
                  />
                  <div className="absolute inset-0 bg-base-300/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button className="btn btn-primary btn-sm" onClick={() => window.open(question.answer_image, '_blank')}>
                      Vergrößern
                    </button>
                  </div>
                </div>
              )}

              <Disclosure>
                {({ open }) => (
                  <div className="bg-base-200 rounded-lg">
                    <div className="p-3">
                      <DisclosureButton className="flex w-full items-center gap-2 text-base-content/70 hover:text-base-content transition-colors">
                        <LightBulbIcon className="h-5 w-5" />
                        <span>Ausführliche Erklärung</span>
                        <ChevronDownIcon className={`ml-auto h-5 w-5 transform transition-transform ${open ? 'rotate-180' : ''}`} />
                      </DisclosureButton>
                      <DisclosurePanel className="mt-3">
                        <div className="prose max-w-none">
                          <ReactMarkdown>
                            {question.explanation_text}
                          </ReactMarkdown>
                        </div>
                        {question.explanation_image && !imageError.explanation && (
                          <div className="relative group mt-3">
                            <img
                              src={question.explanation_image}
                              alt="Zur Erklärung"
                              className="rounded-lg shadow-sm max-h-[30vh] w-full object-contain bg-base-200"
                              onError={() => handleImageError('explanation')}
                            />
                            <div className="absolute inset-0 bg-base-300/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <button className="btn btn-primary btn-sm" onClick={() => window.open(question.explanation_image, '_blank')}>
                                Vergrößern
                              </button>
                            </div>
                          </div>
                        )}
                      </DisclosurePanel>
                    </div>
                  </div>
                )}
              </Disclosure>

              {/* Answer Buttons */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <button
                  className="btn btn-success gap-2"
                  onClick={() => handleAnswer(true)}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  Richtig
                </button>
                <button
                  className="btn btn-error gap-2"
                  onClick={() => handleAnswer(false)}
                >
                  <XCircleIcon className="h-5 w-5" />
                  Falsch
                </button>
              </div>

              {/* Keyboard Shortcuts - Only visible on desktop */}
              <div className="hidden lg:flex items-center justify-center gap-4 text-sm text-base-content/60">
                <div className="flex items-center gap-1">
                  <kbd className="kbd kbd-sm">R</kbd>
                  <span>Richtig</span>
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="kbd kbd-sm">F</kbd>
                  <span>Falsch</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <button
            className="btn btn-primary w-full gap-2"
            onClick={() => setShowAnswer(true)}
          >
            <span>Antwort aufdecken</span>
            <kbd className="kbd kbd-sm hidden lg:inline-flex">Leerzeichen</kbd>
          </button>
        )}
      </div>
    </div>
  );
};

export default Flashcard;